import { isValidHomepage } from '@/utils/clinicFields';

/**
 * 홈페이지 대표 사진 자동 등록 규칙(가입 2단계·치과 정보 수정 공통, 2026-10-26 승인 시안 + 리뷰 반영).
 * 화면 상태와 떨어진 순수 함수만 둔다 — __checks__/homepagePhotoRules.check.ts 가 검사한다.
 *
 * 치과 이미지 목록은 [0] 이 대표 사진, 그 뒤가 추가 사진이다(서버 images·mobileImages 순서 그대로, 저장은 5장까지 — 화면 목록은 [MAX_LIST_IMAGES] 까지).
 * 기본 이미지('/default-clinic.png')는 실제 사진이 0장일 때만 목록에 둔다 — 실제 사진이 1장이라도 있으면 빠진다.
 */

export const DEFAULT_CLINIC_IMAGE = '/default-clinic.png';
/** 대표 포함 치과 이미지 최대 장수(서버 ClinicImageRule.MAX_IMAGES 와 같다) */
export const MAX_CLINIC_IMAGES = 5;
/**
 * 화면 목록에 둘 수 있는 장수(2026-10-30) — 홈페이지 사진은 5장을 넘어도 모두 보여 주고, 저장할 때 5장까지만 고르게 한다
 * ([overLimitCount] 가 0 이어야 저장). 너무 많은 사진을 한 번에 가져오지 않게 상한만 둔다.
 */
export const MAX_LIST_IMAGES = 20;

/**
 * 대표 사진이 어디서 왔는지 — 자동으로 바꿔도 되는지 가른다(꼬리표 "홈페이지 사진"은 따로 — [isHomepagePhoto]).
 * - none: 아직 없음
 * - default: 기본 이미지
 * - auto: 홈페이지 결과로 자동으로 넣은 사진
 * - saved: 치과 정보 수정 화면을 열 때 이미 저장돼 있던 사진(2026-10-30) — 주소를 바꿔 홈페이지를 새로 읽으면
 *   홈페이지 첫 사진이 대표로 들어가고 원래 대표는 지우지 않고 추가 사진 맨 앞으로 내린다([applyAutoMain]).
 * - manual: 회원이 이 화면에서 직접 올렸거나 고른 사진(가입은 저장된 사진 포함) — 자동으로 바꾸지 않는다
 */
export type MainPhotoSource = 'none' | 'default' | 'auto' | 'saved' | 'manual';

export interface ClinicPhotoState {
    images: string[];
    mainSource: MainPhotoSource;
}

const EMPTY: ClinicPhotoState = { images: [], mainSource: 'none' };
const DEFAULT_ONLY: ClinicPhotoState = { images: [DEFAULT_CLINIC_IMAGE], mainSource: 'default' };

/** 실제 사진만(기본 이미지·빈 값 빼고, 같은 주소 한 번) */
const realOf = (images: string[]): string[] =>
    images.filter((url, index) => !!url && url.trim().length > 0 && url !== DEFAULT_CLINIC_IMAGE && images.indexOf(url) === index);

/** 실제 사진 장수(기본 이미지는 세지 않는다) */
export const realPhotoCount = (images: string[]): number => realOf(images).length;

/** 저장된 치과 이미지로 시작 상태 — 비었으면 none, 기본 이미지뿐이면 default, 그 밖은 회원 사진(manual, 기본 이미지는 뺀다) */
export const photoStateOf = (images: string[] | null | undefined): ClinicPhotoState => {
    const list: string[] = (images ?? []).filter((url) => !!url && url.trim().length > 0);
    if (list.length === 0) return EMPTY;
    const real: string[] = realOf(list);
    if (real.length === 0) return DEFAULT_ONLY;
    return { images: real.slice(0, MAX_LIST_IMAGES), mainSource: 'manual' };
};

/**
 * 치과 정보 수정 화면 시작 상태(2026-10-30) — 가입처럼 실제 사진이 0장이면 기본 이미지를 대표로 보여 주고,
 * 저장돼 있던 사진은 'saved'(새 홈페이지 사진이 오면 대표를 넘겨주되 지워지지 않는다).
 */
export const editPhotoStateOf = (images: string[] | null | undefined): ClinicPhotoState => {
    const real: string[] = realOf((images ?? []).filter((url) => !!url && url.trim().length > 0));
    if (real.length === 0) return DEFAULT_ONLY;
    return { images: real.slice(0, MAX_LIST_IMAGES), mainSource: 'saved' };
};

/** 규칙 6 — 새 홈페이지 결과로 대표 사진을 바꿔도 되는 출처인가(직접 올린·고른 사진은 지킨다) */
export const canAutoReplaceMain = (source: MainPhotoSource): boolean => source !== 'manual';

/**
 * 지금 상태에 홈페이지 첫 사진을 대표로 넣을 수 있는가 — 출처가 바꿔도 되는 것이고, 목록 상한([MAX_LIST_IMAGES]) 안일 때.
 * 저장돼 있던 대표(saved)는 지우지 않고 추가 사진으로 내린다 — 5장이 넘으면 저장할 때 회원이 5장으로 줄인다.
 */
export const canApplyAutoMain = (state: ClinicPhotoState): boolean =>
    canAutoReplaceMain(state.mainSource) && realPhotoCount(state.images) < MAX_LIST_IMAGES;

/** 저장하려면 빼야 하는 장수 — 0 이면 저장할 수 있다(5장까지) */
export const overLimitCount = (state: ClinicPhotoState): number => Math.max(0, realPhotoCount(state.images) - MAX_CLINIC_IMAGES);

/** 5장이 넘을 때 [저장]·[회원가입] 을 막는 안내 */
export const overLimitMessage = (over: number): string => `치과 이미지는 ${MAX_CLINIC_IMAGES}장까지 저장할 수 있어요. ${over}장을 빼 주세요.`;

/** 대표 사진 자리에 [url](실제 사진) — 기존 대표는 빠지고 추가 사진은 그대로 */
export const replaceMain = (state: ClinicPhotoState, url: string, source: MainPhotoSource): ClinicPhotoState => {
    const rest: string[] = realOf(state.images.slice(1)).filter((u) => u !== url);
    return { images: [url, ...rest].slice(0, MAX_LIST_IMAGES), mainSource: source };
};

/**
 * 홈페이지 첫 사진을 자동으로 대표에 — 바꾸면 안 되는 상태면 null.
 * - 비었음·기본 이미지·자동 사진: 대표 자리를 바꾼다(자동 사진은 빠지고 추가 사진은 그대로).
 * - 저장돼 있던 사진(saved): 새 사진이 대표, 원래 대표는 추가 사진 맨 앞으로(지우지 않는다). 목록 상한이면 null.
 */
export const applyAutoMain = (state: ClinicPhotoState, savedUrl: string): ClinicPhotoState | null => {
    if (!canApplyAutoMain(state)) return null;
    if (state.mainSource !== 'saved') return replaceMain(state, savedUrl, 'auto');
    const real: string[] = realOf(state.images).filter((u) => u !== savedUrl);
    return { images: [savedUrl, ...real].slice(0, MAX_LIST_IMAGES), mainSource: 'auto' };
};

/**
 * 규칙 5·6(가입) — 홈페이지 없음·주소 비움·후보 0장·읽기 실패:
 * 대표가 비었으면 기본 이미지. 자동으로 넣은 사진이면 빼고, 추가 사진이 있으면 첫 추가 사진을 대표로(회원이 넣은 사진 = manual), 없으면 기본 이미지.
 * 직접 올린 사진·이미 기본 이미지면 그대로.
 */
export const applyFallbackMain = (state: ClinicPhotoState): ClinicPhotoState => {
    if (realPhotoCount(state.images) === 0) return DEFAULT_ONLY;
    if (state.mainSource !== 'auto') return state;
    const rest: string[] = realOf(state.images.slice(1));
    return rest.length > 0 ? { images: rest, mainSource: 'manual' } : DEFAULT_ONLY;
};

/**
 * 규칙 6(수정 화면) — 실패·주소 비움·저장된 주소로 되돌림 때 자동으로 넣었던 대표를 저장돼 있던 원래 대표로 되돌린다
 * (추가 사진은 유지 — saved 대표를 내려 뒀던 자리는 빠지고 원래 대표가 다시 맨 앞, 출처도 원래대로).
 * 원래 대표가 기본 이미지·없음이면: 추가 사진이 있으면 첫 추가 사진이 대표, 없으면 원래 상태(기본 이미지·없음).
 */
export const restoreOriginalMain = (state: ClinicPhotoState, original: ClinicPhotoState): ClinicPhotoState => {
    if (state.mainSource !== 'auto') return state;
    const rest: string[] = realOf(state.images.slice(1));
    const originalMain: string | undefined = realOf(original.images)[0];
    if (!originalMain) return rest.length > 0 ? { images: rest, mainSource: 'manual' } : original;
    return { images: [originalMain, ...rest.filter((u) => u !== originalMain)].slice(0, MAX_LIST_IMAGES), mainSource: original.mainSource };
};

/** 한 장 더 넣을 수 있는가 — 기본 이미지는 새 사진이 들어오면 빠지므로 세지 않는다 */
export const canAddPhoto = (state: ClinicPhotoState): boolean => realPhotoCount(state.images) < MAX_LIST_IMAGES;

/**
 * 추가 사진 한 장 — 대표 아래 맨 뒤에. 실제 사진이 없으면(비었음·기본 이미지) 대표 자리에 넣는다(회원이 고른 사진 = manual).
 * 목록 상한([MAX_LIST_IMAGES])이거나 이미 있는 주소면 null.
 */
export const addPhoto = (state: ClinicPhotoState, url: string): ClinicPhotoState | null => {
    if (!canAddPhoto(state) || state.images.includes(url)) return null;
    const real: string[] = realOf(state.images);
    if (real.length === 0) return { images: [url], mainSource: 'manual' };
    return { images: [...real, url], mainSource: state.mainSource };
};

/**
 * [다른 사진으로 바꾸기]에서 고른 사진 — 대표 자리에(회원이 고른 사진 = manual).
 * 원래 대표가 직접 올린·고른 사진·저장돼 있던 사진이면 버리지 않고 추가 사진 맨 앞으로 내린다(목록 상한 넘으면 null).
 * 원래 대표가 자동으로 넣은 사진·기본 이미지면 빠진다.
 */
export const swapMain = (state: ClinicPhotoState, url: string): ClinicPhotoState | null => {
    const real: string[] = realOf(state.images);
    const oldMain: string | undefined = real[0];
    const keepOld: boolean = state.mainSource === 'manual' || state.mainSource === 'saved';
    if (!keepOld || !oldMain || oldMain === url) return replaceMain(state, url, 'manual');
    const next: string[] = [url, oldMain, ...real.slice(1).filter((u) => u !== url)];
    if (next.length > MAX_LIST_IMAGES) return null;
    return { images: next, mainSource: 'manual' };
};

/** 추가 사진 빼기([×]) — 대표([0])를 빼면 다음 사진이 대표(회원이 정한 것 = manual), 하나도 없으면 기본 이미지 */
export const removePhoto = (state: ClinicPhotoState, index: number): ClinicPhotoState => {
    if (index < 0 || index >= state.images.length) return state;
    const images: string[] = realOf(state.images.filter((_, i) => i !== index));
    if (images.length === 0) return DEFAULT_ONLY;
    return { images, mainSource: index === 0 ? 'manual' : state.mainSource };
};

/** 추가 사진 타일을 누르면 대표로 — 그 사진이 맨 앞, 나머지 순서는 유지(회원이 고른 것 = manual, 기본 이미지는 뺀다) */
export const makeMain = (state: ClinicPhotoState, index: number): ClinicPhotoState => {
    if (index <= 0 || index >= state.images.length) return state;
    const picked: string = state.images[index];
    if (picked === DEFAULT_CLINIC_IMAGE) return state;
    return { images: realOf([picked, ...state.images.filter((_, i) => i !== index)]), mainSource: 'manual' };
};

/** 가입·치과 정보 수정 요청 이미지 — 대표가 [0], 실제 사진만(기본 이미지는 0장일 때만 한 장). 5장 넘으면 화면이 먼저 막는다([overLimitCount]) */
export const signupImagesOf = (state: ClinicPhotoState): string[] => {
    const real: string[] = realOf(state.images).slice(0, MAX_CLINIC_IMAGES);
    return real.length > 0 ? real : [DEFAULT_CLINIC_IMAGE];
};

/** 꼬리표 "홈페이지 사진" — 대표 주소가 홈페이지에서 저장한 주소인가(출처 판단 mainSource 와 따로) */
export const isHomepagePhoto = (url: string | undefined, savedByCandidate: Record<string, string>): boolean =>
    !!url && Object.values(savedByCandidate).includes(url);

/* ─── 주소 경합(규칙 2) ─── */

/** 같은 홈페이지인지 가르는 열쇠 — 앞뒤 공백·http(s)://·끝 / ·대소문자를 무시한다. 형식이 틀리면 빈 문자열 */
export const homepageKey = (raw: string | null | undefined): string => {
    const trimmed: string = (raw ?? '').trim();
    if (!isValidHomepage(trimmed)) return '';
    return trimmed.replace(/^https?:\/\//i, '').replace(/\/+$/, '').toLowerCase();
};

/** 지금 읽어야 하는가 — 형식이 맞고, 마지막으로 읽은 주소와 다를 때만(같은 주소 다시 읽지 않기, [force] 면 같아도) */
export const shouldReadHomepage = (raw: string | null | undefined, lastReadKey: string | null, force: boolean = false): boolean => {
    const key: string = homepageKey(raw);
    if (!key) return false;
    return force || key !== lastReadKey;
};

/** 늦게 온 결과인가 — 요청한 주소와 지금 칸의 주소가 다르거나, 그 뒤 새 읽기가 시작됐으면 버린다 */
export const isStaleHomepageResult = (requestedKey: string, currentRaw: string | null | undefined, requestSeq: number, latestSeq: number): boolean =>
    requestSeq !== latestSeq || homepageKey(currentRaw) !== requestedKey;

/** 수정 화면 시작 열쇠 — 저장된 홈페이지 주소(주소를 안 바꾸고 칸에서 벗어나면 읽지 않는다) */
export const initialHomepageKey = (savedUrl: string | null | undefined): string | null => homepageKey(savedUrl) || null;
